import type { BridgeUpgradeFeatureId } from "./bridgeUpgradeCopy";

/**
 * The one set of stroke icons for the HappyBridge upgrade offer, shared by
 * `BridgeUpgradeModal`, `BridgeUpgradeCta` (the announcement card) and
 * `BridgeUpgradeBanner` (the app-wide strip). The four feature SVGs used to be copy-pasted into
 * two files and paired to a feature by array index — the same shape existing twice, with nothing
 * to stop them drifting apart, and index-pairing carrying the same fragility a shared id was
 * supposed to remove.
 *
 * Looked up by `BridgeUpgradeFeatureId`, never by position. Colour is the one thing that
 * legitimately differs between the two surfaces (the modal strokes `max-800` on white, the
 * banner `max-500` on the dark card), so it isn't baked in here: every path uses
 * `stroke="currentColor"` and the caller supplies colour (and any sizing/margin) via
 * `className` on the wrapping element.
 */

const PATHS: Record<BridgeUpgradeFeatureId, React.ReactNode> = {
    // Heartbeat / steady line — reliability
    reliability: (
        <path
            d="M22 12h-4l-3 8-6-16-3 8H2"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    ),
    // Sync / refresh
    sync: (
        <path
            d="M3 12a9 9 0 0 1 15.3-6.4M21 12a9 9 0 0 1-15.3 6.4M17 3v5h-5M7 21v-5h5"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    ),
    // Palette / color codes — the small details that carry over
    details: (
        <>
            <path
                d="M12 21a9 9 0 1 1 0-18c4.97 0 9 3.5 9 7.5 0 2.49-2.01 4.5-4.5 4.5H14a1.5 1.5 0 0 0-1 2.6c.3.3.5.7.5 1.15 0 1.3-1.1 2.25-1.5 2.25Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            <circle cx="7.5" cy="10.5" r="1.25" fill="currentColor" />
            <circle cx="11.5" cy="7" r="1.25" fill="currentColor" />
            <circle cx="16" cy="10" r="1.25" fill="currentColor" />
        </>
    ),
    // Stacked layers — something to build on
    foundation: (
        <path
            d="m12 3 9 4.5-9 4.5-9-4.5L12 3ZM3 12l9 4.5 9-4.5M3 16.5 12 21l9-4.5"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    ),
};

interface BridgeUpgradeFeatureIconProps {
    id: BridgeUpgradeFeatureId;
    /** Sizing/margin plus a text-color utility for the stroke — see the module doc comment. */
    className?: string;
}

export function BridgeUpgradeFeatureIcon({ id, className }: BridgeUpgradeFeatureIconProps): React.JSX.Element {
    return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className={className}>
            {PATHS[id]}
        </svg>
    );
}
